import {
  ArrowLeft,
  FileText,
  Mail,
  Phone,
  Save,
  Shield,
  Upload,
  User,
} from "lucide-react";
import { useState } from "react";
import { toast } from "react-hot-toast";
import { useMutation } from "react-query";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { api } from "../../utils/api";

const CreateDriver = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    gender: "",
    aadharNo: "",
    licenseNo: "",
    panNo: "",
    role: "",
    bankDetails: {
      accountHolderName: "",
      accountNumber: "",
      bankName: "",
      ifscCode: "",
      branch: "",
      UPIID: "",
    },
  });

  const [files, setFiles] = useState({
    aadhar: null,
    drivingLicense: null,
    pvc: null,
    panCard: null,
    driverPhoto: null,
    passbookImage: null,
  });

  const createDriverMutation = useMutation(
    (data) =>
      api.post("/drivers", data, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }),
    {
      onSuccess: () => {
        toast.success("Driver created successfully");
        navigate("/drivers");
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || "Failed to create driver");
      },
    },
  );

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.includes(".")) {
      const [parent, child] = name.split(".");
      setFormData((prev) => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleFileChange = (e, fieldName) => {
    const file = e.target.files[0];
    if (file) {
      setFiles((prev) => ({
        ...prev,
        [fieldName]: file,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !formData.name ||
      !formData.phone ||
      !formData.email ||
      !formData.gender ||
      !formData.aadharNo ||
      !formData.licenseNo ||
      !formData.panNo ||
      !formData.role
    ) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);
    try {
      const formDataToSend = new FormData();

      // Append general form fields
      Object.keys(formData).forEach((key) => {
        if (key === "bankDetails") {
          formDataToSend.append(key, JSON.stringify(formData[key]));
        } else {
          formDataToSend.append(key, formData[key]);
        }
      });

      // Append files if they exist
      Object.keys(files).forEach((key) => {
        if (files[key]) {
          formDataToSend.append(key, files[key]);
        }
      });

      await createDriverMutation.mutateAsync(formDataToSend);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!hasPermission("drivers", "write")) {
    return (
      <div className="empty-state">
        <Shield className="empty-state-icon" />
        <h3 className="empty-state-title">Access Denied</h3>
        <p className="empty-state-description">
          You don't have permission to create drivers.
        </p>
        <Link to="/drivers" className="btn btn-primary mt-4">
          Back to Drivers
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="page-header">
        <div className="px-4 py-5 sm:px-6">
          <div className="flex items-center space-x-4">
            <Link to="/drivers" className="text-gray-400 hover:text-gray-600">
              <ArrowLeft className="h-6 w-6" />
            </Link>
            <div>
              <h1 className="page-title">Create Driver</h1>
              <p className="page-subtitle">Add a new driver to the platform</p>
            </div>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="card">
        <div className="card-body">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Basic Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="form-label">
                    <User className="h-4 w-4 inline mr-2" />
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="input"
                    placeholder="Enter driver's full name"
                    required
                  />
                </div>

                <div>
                  <label className="form-label">
                    <Phone className="h-4 w-4 inline mr-2" />
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="input"
                    placeholder="Enter phone number"
                    required
                  />
                </div>

                <div>
                  <label className="form-label">
                    <Mail className="h-4 w-4 inline mr-2" />
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="input"
                    placeholder="Enter email address"
                    required
                  />
                </div>

                <div>
                  <label className="form-label">Gender *</label>
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleInputChange}
                    className="input"
                    required
                  >
                    <option value="">Select gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="form-label">Role</label>
                  <select
                    name="role"
                    value={formData.role}
                    onChange={handleInputChange}
                    className="input"
                    required
                  >
                    <option value="">Select role</option>
                    <option value="dco">DCO</option>
                    <option value="salaried">Salaried</option>
                    <option value="actingdriver">Acting Driver</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Identity Documents */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Identity Documents
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="form-label">
                    <Shield className="h-4 w-4 inline mr-2" />
                    Aadhar Number *
                  </label>
                  <input
                    type="text"
                    name="aadharNo"
                    value={formData.aadharNo}
                    onChange={handleInputChange}
                    className="input"
                    placeholder="Enter Aadhar number"
                    required
                  />
                </div>

                <div>
                  <label className="form-label">
                    <FileText className="h-4 w-4 inline mr-2" />
                    Driving License *
                  </label>
                  <input
                    type="text"
                    name="licenseNo"
                    value={formData.licenseNo}
                    onChange={handleInputChange}
                    className="input"
                    placeholder="Enter license number"
                    required
                  />
                </div>

                <div>
                  <label className="form-label">
                    <FileText className="h-4 w-4 inline mr-2" />
                    PAN Number *
                  </label>
                  <input
                    type="text"
                    name="panNo"
                    value={formData.panNo}
                    onChange={handleInputChange}
                    className="input"
                    placeholder="Enter PAN number"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Bank Details */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Bank Details
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Account Holder Name</label>
                  <input
                    type="text"
                    name="bankDetails.accountHolderName"
                    value={formData.bankDetails.accountHolderName}
                    onChange={handleInputChange}
                    className="input"
                    placeholder="Enter account holder name"
                  />
                </div>

                <div>
                  <label className="form-label">Account Number</label>
                  <input
                    type="text"
                    name="bankDetails.accountNumber"
                    value={formData.bankDetails.accountNumber}
                    onChange={handleInputChange}
                    className="input"
                    placeholder="Enter account number"
                  />
                </div>

                <div>
                  <label className="form-label">Bank Name</label>
                  <input
                    type="text"
                    name="bankDetails.bankName"
                    value={formData.bankDetails.bankName}
                    onChange={handleInputChange}
                    className="input"
                    placeholder="Enter bank name"
                  />
                </div>

                <div>
                  <label className="form-label">IFSC Code</label>
                  <input
                    type="text"
                    name="bankDetails.ifscCode"
                    value={formData.bankDetails.ifscCode}
                    onChange={handleInputChange}
                    className="input"
                    placeholder="Enter IFSC code"
                  />
                </div>

                <div>
                  <label className="form-label">Branch</label>
                  <input
                    type="text"
                    name="bankDetails.branch"
                    value={formData.bankDetails.branch}
                    onChange={handleInputChange}
                    className="input"
                    placeholder="Enter branch name"
                  />
                </div>

                <div>
                  <label className="form-label">UPI ID</label>
                  <input
                    type="text"
                    name="bankDetails.UPIID"
                    value={formData.bankDetails.UPIID}
                    onChange={handleInputChange}
                    className="input"
                    placeholder="Enter UPI ID"
                  />
                </div>
              </div>
            </div>

            {/* Document Upload */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Documents
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  "aadhar",
                  "drivingLicense",
                  "pvc",
                  "panCard",
                  "driverPhoto",
                  "passbookImage",
                ].map((doc) => (
                  <div
                    key={doc}
                    className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
                      files[doc] ? "border-green-500 bg-green-50" : "border-gray-300"
                    }`}
                  >
                    <Upload className={`h-8 w-8 mx-auto mb-2 ${files[doc] ? "text-green-500" : "text-gray-400"}`} />
                    <p className="text-sm font-medium text-gray-900 capitalize">
                      {doc.replace(/([A-Z])/g, " $1").replace("_", " ")}
                    </p>
                    {files[doc] ? (
                      <p className="text-xs text-green-600 mt-1 font-medium truncate max-w-full px-2" title={files[doc].name}>
                        {files[doc].name}
                      </p>
                    ) : (
                      <p className="text-xs text-gray-500 mt-1">
                        Upload {doc.replace(/([A-Z])/g, " $1").replace("_", " ")}
                      </p>
                    )}
                    <input
                      type="file"
                      accept="image/*,.pdf"
                      className="hidden"
                      id={doc}
                      onChange={(e) => handleFileChange(e, doc)}
                    />
                    <label
                      htmlFor={doc}
                      className={`btn btn-sm mt-2 cursor-pointer ${
                        files[doc] ? "btn-primary" : "btn-outline"
                      }`}
                    >
                      {files[doc] ? "Change File" : "Choose File"}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200">
              <Link to="/drivers" className="btn btn-outline">
                Cancel
              </Link>
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn btn-primary"
              >
                {isSubmitting ? (
                  <>
                    <div className="loading-spinner mr-2"></div>
                    Creating...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Create Driver
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateDriver;
